import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Layout from '../components/Layout';
import MiniDashboard from '../components/MiniDashboard';
import { CreditCard, Download, Eye, Upload, Check, X, Loader2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function IDCardManagement({ user, onLogout }) {
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [generating, setGenerating] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [dashboardStats, setDashboardStats] = useState(null);

  useEffect(() => {
    fetchEmployees();
    fetchDashboardStats();
  }, []);

  const fetchDashboardStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/dashboard/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDashboardStats(response.data);
    } catch (error) {
      console.error('Failed to fetch dashboard stats:', error);
    }
  };

  const fetchEmployees = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/employees?limit=1000`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEmployees(response.data);
      setLoading(false);
    } catch (err) {
      console.error('Failed to fetch employees:', err);
      setLoading(false);
    }
  };

  const handlePreview = async (employeeId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/id-card/preview/${employeeId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        setPreviewData(response.data.data);
        setShowPreview(true);
      }
    } catch (err) {
      alert('Failed to preview ID card data');
    }
  };

  const handleGenerate = async (employeeId) => {
    try {
      setGenerating(true);
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/id-card/generate`, 
        { employee_id: employeeId },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      
      if (response.data.success) {
        alert('ID card generated successfully!');
        fetchEmployees();
      }
    } catch (err) {
      alert('Error generating ID card');
    } finally {
      setGenerating(false);
    }
  };

  const handleBulkGenerate = async () => {
    if (selectedEmployees.length === 0) {
      alert('Please select at least one employee');
      return;
    }

    try {
      setGenerating(true);
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/id-card/bulk-generate`,
        selectedEmployees,
        { headers: { Authorization: `Bearer ${token}` }}
      );
      
      if (response.data.success) {
        alert(`Generated ${response.data.success_count} ID cards successfully!`);
        setSelectedEmployees([]);
        fetchEmployees();
      }
    } catch (err) {
      alert('Error generating ID cards');
    } finally {
      setGenerating(false);
    }
  };

  const handleDownload = async (employeeId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/id-card/download/${employeeId}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `ID_Card_${employeeId}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      alert('Failed to download ID card');
    }
  };

  const toggleEmployeeSelection = (employeeId) => {
    setSelectedEmployees(prev =>
      prev.includes(employeeId)
        ? prev.filter(id => id !== employeeId)
        : [...prev, employeeId]
    );
  };

  const selectAll = () => {
    if (selectedEmployees.length === employees.length) {
      setSelectedEmployees([]);
    } else {
      setSelectedEmployees(employees.map(emp => emp.id));
    }
  };

  const generatedCount = employees.filter(e => e.id_card_generated).length;

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="p-6">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-3xl font-bold" style={{color: '#2b3380'}}>ID Card Management</h1>
          <button
            onClick={handleBulkGenerate}
            disabled={selectedEmployees.length === 0 || generating}
            className="px-6 py-3 rounded-lg text-white font-medium flex items-center gap-2"
            style={{backgroundColor: selectedEmployees.length === 0 ? '#cccccc' : '#2b3380'}}
          >
            {generating ? (
              <><Loader2 className="w-5 h-5 animate-spin" />Generating...</>
            ) : (
              <><Upload className="w-5 h-5" />Bulk Generate ({selectedEmployees.length})</>
            )}
          </button>
        </div>

        {/* ID Card Management Mini Dashboard */}
        {dashboardStats && (
          <MiniDashboard
            title="ID Card Management Overview"
            icon="🪪"
            bgColor="#673ab7"
            stats={[
              {
                label: 'Total Employees',
                value: employees.length,
                subtitle: 'In system',
                color: '#2b3380'
              },
              {
                label: 'Cards Generated',
                value: generatedCount,
                subtitle: 'Completed',
                color: '#4caf50'
              },
              {
                label: 'Pending',
                value: employees.length - generatedCount,
                subtitle: 'Not generated',
                color: '#ff9800'
              },
              {
                label: 'Selected',
                value: selectedEmployees.length,
                subtitle: 'For bulk action',
                color: '#673ab7'
              }
            ]}
          />
        )}

        {/* Bulk Actions */}
        <div className="bg-white rounded-lg shadow-md p-4 mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <input
                type="checkbox"
                checked={selectedEmployees.length === employees.length && employees.length > 0}
                onChange={selectAll}
                className="w-4 h-4 rounded"
              />
              <span className="text-sm font-medium">
                {selectedEmployees.length} of {employees.length} selected
              </span>
            </div>
          </div>
        </div>

        {/* Employee List */}
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <table className="w-full">
            <thead style={{backgroundColor: '#f5f7fa'}}>
              <tr>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Select</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Employee ID</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Name</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Designation</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Status</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Actions</th>
              </tr>
            </thead>
            <tbody>
              {employees.map((employee) => (
                <tr key={employee.id} className="border-t hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <input
                      type="checkbox"
                      checked={selectedEmployees.includes(employee.id)}
                      onChange={() => toggleEmployeeSelection(employee.id)}
                      className="w-4 h-4 rounded"
                    />
                  </td>
                  <td className="px-4 py-3 font-medium">{employee.employee_id}</td>
                  <td className="px-4 py-3">{employee.first_name} {employee.last_name}</td>
                  <td className="px-4 py-3 text-gray-600">{employee.designation}</td>
                  <td className="px-4 py-3">
                    {employee.id_card_generated ? (
                      <span className="px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        <Check className="w-3 h-3 inline mr-1" />Generated
                      </span>
                    ) : (
                      <span className="px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                        <X className="w-3 h-3 inline mr-1" />Not Generated
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handlePreview(employee.id)}
                        className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg"
                        title="Preview"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                      {employee.id_card_generated ? (
                        <button
                          onClick={() => handleDownload(employee.id)}
                          className="p-2 text-green-600 hover:bg-green-50 rounded-lg"
                          title="Download"
                        >
                          <Download className="w-4 h-4" />
                        </button>
                      ) : (
                        <button
                          onClick={() => handleGenerate(employee.id)}
                          disabled={generating}
                          className="px-3 py-1 text-sm text-white rounded-lg"
                          style={{backgroundColor: '#2b3380'}}
                        >
                          Generate
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Preview Modal */}
      {showPreview && previewData && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold" style={{color: '#2b3380'}}>ID Card Preview</h2>
              <button onClick={() => setShowPreview(false)} className="p-2 hover:bg-gray-100 rounded-lg">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="space-y-3 text-sm">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <span className="font-medium text-gray-600">Name:</span>
                  <p className="text-gray-900">{previewData.employee_name}</p>
                </div>
                <div>
                  <span className="font-medium text-gray-600">Designation:</span>
                  <p className="text-gray-900">{previewData.designation}</p>
                </div>
                <div>
                  <span className="font-medium text-gray-600">Employee ID:</span>
                  <p className="text-gray-900">{previewData.employee_id_display}</p>
                </div>
                <div>
                  <span className="font-medium text-gray-600">Contact:</span>
                  <p className="text-gray-900">{previewData.contact_number}</p>
                </div>
                <div className="col-span-2">
                  <span className="font-medium text-gray-600">Email:</span>
                  <p className="text-gray-900">{previewData.email}</p>
                </div>
              </div>
            </div>
            <div className="mt-6 flex justify-end">
              <button
                onClick={() => setShowPreview(false)}
                className="px-4 py-2 text-white rounded-lg"
                style={{backgroundColor: '#2b3380'}}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}

export default IDCardManagement;